import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../i18n";
import { useLangStore } from "../i18n/langStore";

interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  is_manual: boolean;
  created_at: string;
  read: boolean;
}

// v3 liquid glass: el color del tipo vive solo en el punto semántico.
const TYPE_COLOR: Record<string, string> = {
  info:    "#3b82f6",  // azul semántico (= TopBar), no el teal de marca
  warning: "#f59e0b",
  success: "#10b981",
  feature: "#8b5cf6",
};

function relativeTime(iso: string, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("notifications.now");
  if (mins < 60) return t("notifications.minutesAgo", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t(hrs !== 1 ? "notifications.hoursAgo" : "notifications.hourAgo", { n: hrs });
  const days = Math.floor(hrs / 24);
  if (days === 1) return t("notifications.yesterday");
  if (days < 7) return t("notifications.daysAgo", { n: days });
  return new Date(iso).toLocaleDateString(t("notifications.dateLocale"), { day: "numeric", month: "short", year: "numeric" });
}

export default function NotificationsPage() {
  const t = useT();
  const lang = useLangStore(s => s.lang);
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  async function load() {
    try {
      const data = await api.get<Notification[]>(`/notifications?lang=${lang}`);
      setItems(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  // Recarga al cambiar el idioma para repintar los textos que renderiza el backend.
  useEffect(() => { load(); }, [lang]);

  async function markRead(id: string) {
    try {
      await api.post(`/notifications/${id}/read`, {});
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch {
      // ignore
    }
  }

  async function markAll() {
    setMarkingAll(true);
    try {
      await api.post("/notifications/read-all", {});
      setItems(prev => prev.map(n => ({ ...n, read: true })));
    } catch {
      // ignore
    } finally {
      setMarkingAll(false);
    }
  }

  const unread = items.filter(n => !n.read);

  return (
    <div style={{ padding: "2rem", maxWidth: 680, margin: "0 auto", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.75rem" }}>
        <h1 style={{ fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", margin: 0 }}>
          {t("notifications.title")}
          {unread.length > 0 && (
            <span style={{
              marginLeft: 10, background: "var(--clr-org-primary)", color: "#fff",
              borderRadius: 999, fontSize: "0.7rem", fontWeight: 700,
              padding: "2px 8px", verticalAlign: "middle",
            }}>
              {unread.length}
            </span>
          )}
        </h1>
        {unread.length > 0 && (
          <button
            onClick={markAll}
            disabled={markingAll}
            style={{
              fontSize: "0.78rem", fontWeight: 500, padding: "0.42rem 1rem",
              border: "1px solid var(--clr-border)",
              borderRadius: 999, cursor: "pointer",
              background: "var(--clr-surface)",
              color: "var(--clr-text3)",
            }}
          >
            {markingAll ? t("notifications.markingAll") : t("notifications.markAllRead")}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: "var(--clr-text4)", textAlign: "center", padding: "3rem" }}>{t("notifications.loading")}</div>
      ) : items.length === 0 ? (
        <div style={{
          background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
          borderRadius: 18, padding: "3rem", textAlign: "center",
          backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
          boxShadow: "var(--clr-shadow-sm)",
          color: "var(--clr-text4)", fontSize: "0.9rem",
        }}>
          {t("notifications.empty")}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {items.map(n => (
            <div
              key={n.id}
              onClick={() => !n.read && markRead(n.id)}
              onMouseEnter={e => {
                if (!n.read) {
                  e.currentTarget.style.boxShadow = "var(--clr-shadow)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "var(--clr-shadow-sm)";
                e.currentTarget.style.transform = "none";
              }}
              style={{
                background: "var(--clr-surface)",
                border: "1px solid var(--clr-border)",
                borderRadius: 18,
                padding: "1rem 1.25rem",
                backdropFilter: "var(--clr-blur)",
                WebkitBackdropFilter: "var(--clr-blur)",
                boxShadow: "var(--clr-shadow-sm)",
                cursor: !n.read ? "pointer" : "default",
                opacity: n.read ? 0.75 : 1,
                transition: "transform 0.25s cubic-bezier(.25,.6,.3,1), box-shadow 0.25s, opacity 0.15s",
                display: "flex",
                gap: "0.75rem",
                alignItems: "flex-start",
              }}
            >
              {/* Unread dot — en el color semántico del tipo */}
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: n.read ? "transparent" : (TYPE_COLOR[n.type] ?? "var(--clr-org-primary)"), flexShrink: 0, marginTop: 6 }} />

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.3rem" }}>
                  <span style={{
                    fontWeight: n.read ? 500 : 700,
                    fontSize: "0.875rem",
                    color: "var(--clr-text)",
                  }}>
                    {n.title}
                  </span>
                  {(() => { const tc = TYPE_COLOR[n.type] ?? "var(--clr-org-primary)"; return (
                    // Tipo = cápsula tintada suave SIN punto (el punto es de estados)
                    <span style={{
                      display: "inline-flex", alignItems: "center",
                      background: tc + "1e", color: tc,
                      borderRadius: 999, padding: "2px 9px",
                      fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase",
                    }}>
                      {n.type}
                    </span>
                  ); })()}
                </div>
                <p style={{ margin: 0, fontSize: "0.83rem", color: "var(--clr-text3)", lineHeight: 1.5 }}>
                  {n.body}
                </p>
                <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", marginTop: "0.4rem" }}>
                  {relativeTime(n.created_at, t)}
                  {!n.read && (
                    <span style={{ marginLeft: 8, color: "var(--clr-org-primary)", fontWeight: 600 }}>
                      · {t("notifications.clickToMark")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
